import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
  Check as CheckIcon,
  DoneAll as DoneAllIcon
} from '@mui/icons-material';
import { Message, UserRole } from '../../utils/types';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
  showAvatar: boolean;
  showTimestamp: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isOwn,
  showAvatar,
  showTimestamp
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // الحصول على لون الدور
  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return theme.palette.error.main;
      case UserRole.BRANCH_MANAGER:
        return theme.palette.warning.main;
      case UserRole.WAREHOUSE_MANAGER:
        return theme.palette.info.main;
      case UserRole.TECHNICIAN:
        return theme.palette.success.main;
      default:
        return theme.palette.primary.main;
    }
  };

  // الحصول على نص الدور
  const getRoleText = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return 'مدير';
      case UserRole.BRANCH_MANAGER:
        return 'مدير فرع';
      case UserRole.WAREHOUSE_MANAGER:
        return 'مدير مخزن';
      case UserRole.TECHNICIAN:
        return 'فني';
      case UserRole.CASHIER:
        return 'كاشير';
      case UserRole.INVENTORY_CLERK:
        return 'موظف مخزون';
      case UserRole.SALES:
        return 'مبيعات';
      default:
        return 'موظف';
    }
  };

  // تنسيق الوقت
  const formatTime = (date: Date) => {
    try {
      return format(date, 'HH:mm', { locale: ar });
    } catch (error) {
      return '';
    }
  };

  // تنسيق التاريخ والوقت الكامل
  const formatFullDateTime = (date: Date) => {
    try {
      return format(date, 'dd/MM/yyyy HH:mm', { locale: ar });
    } catch (error) {
      return '';
    }
  };

  // فتح قائمة الخيارات
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  // إغلاق قائمة الخيارات
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // تحديد لون الخلفية
  const getBubbleColor = () => {
    if (isOwn) {
      return theme.palette.primary.main;
    }
    return theme.palette.background.paper;
  };

  // تحديد لون النص
  const getTextColor = () => {
    if (isOwn) {
      return theme.palette.primary.contrastText;
    }
    return theme.palette.text.primary;
  };

  // الحصول على أيقونة حالة القراءة
  const getReadStatusIcon = () => {
    if (!isOwn) return null;

    if (message.readBy.length > 1) { // أكثر من المرسل
      return <DoneAllIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />;
    } else {
      return <CheckIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isOwn ? 'row-reverse' : 'row',
        alignItems: 'flex-end',
        mb: showTimestamp ? 2 : 0.5,
        mx: 1
      }}
    >
      {/* الصورة الرمزية */}
      {showAvatar && !isOwn && (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: getRoleColor(message.senderRole),
            fontSize: '0.875rem',
            mr: 1
          }}
        >
          {message.senderName.charAt(0)}
        </Avatar>
      )}

      {/* المساحة الفارغة للمحاذاة */}
      {!showAvatar && !isOwn && (
        <Box sx={{ width: 40 }} />
      )}

      {/* محتوى الرسالة */}
      <Box
        sx={{
          maxWidth: '70%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isOwn ? 'flex-end' : 'flex-start'
        }}
      >
        {/* اسم المرسل والدور (للرسائل الواردة فقط) */}
        {showAvatar && !isOwn && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, px: 1 }}>
            <Typography variant="caption" fontWeight="bold" color="text.primary">
              {message.senderName}
            </Typography>
            <Chip
              size="small"
              label={getRoleText(message.senderRole)}
              sx={{
                height: 16,
                fontSize: '0.65rem',
                bgcolor: alpha(getRoleColor(message.senderRole), 0.1),
                color: getRoleColor(message.senderRole),
                '& .MuiChip-label': {
                  px: 0.5
                }
              }}
            />
          </Box>
        )}

        {/* فقاعة الرسالة */}
        <Paper
          elevation={1}
          sx={{
            p: 1.5,
            bgcolor: getBubbleColor(),
            color: getTextColor(),
            borderRadius: 2,
            borderTopLeftRadius: isOwn ? 2 : (showAvatar ? 0.5 : 2),
            borderTopRightRadius: isOwn ? (showAvatar ? 0.5 : 2) : 2,
            position: 'relative',
            '&:hover .message-actions': {
              opacity: 1
            }
          }}
        >
          {/* محتوى الرسالة */}
          <Typography
            variant="body2"
            sx={{
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.4
            }}
          >
            {message.content}
          </Typography>

          {/* معلومات إضافية */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 0.5,
              gap: 1
            }}
          >
            {/* الوقت */}
            <Typography
              variant="caption"
              sx={{
                color: isOwn ? alpha(theme.palette.primary.contrastText, 0.7) : 'text.secondary',
                fontSize: '0.7rem'
              }}
            >
              {formatTime(message.timestamp)}
              {message.isEdited && ' (معدلة)'}
            </Typography>

            {/* حالة القراءة وأزرار الإجراءات */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {getReadStatusIcon()}
              
              {/* زر الخيارات */}
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                className="message-actions"
                sx={{
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  p: 0.25,
                  color: isOwn ? theme.palette.primary.contrastText : 'text.secondary'
                }}
              >
                <MoreVertIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          </Box>
        </Paper>

        {/* الطابع الزمني الكامل */}
        {showTimestamp && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              mt: 0.5,
              px: 1,
              fontSize: '0.7rem',
              textAlign: isOwn ? 'right' : 'left'
            }}
          >
            {formatFullDateTime(message.timestamp)}
          </Typography>
        )}
      </Box>

      {/* قائمة الخيارات */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: isOwn ? 'left' : 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: isOwn ? 'left' : 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleMenuClose}>
          <ReplyIcon sx={{ mr: 1, fontSize: 18 }} />
          <Typography variant="body2">رد</Typography>
        </MenuItem>
        
        {isOwn && (
          <MenuItem onClick={handleMenuClose}>
            <EditIcon sx={{ mr: 1, fontSize: 18 }} />
            <Typography variant="body2">تعديل</Typography>
          </MenuItem>
        )}
        
        {isOwn && (
          <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
            <Typography variant="body2">حذف</Typography>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default MessageBubble;
